import { useCallback, useEffect, useRef, useState } from 'react'
import { logoutPlayer } from '../game/playerApi'
import { abandonSoloSession, getSoloSession } from '../game/soloApi'
import { playSfx } from '../sound'

interface HealthResponse {
  status: string
  units: number
  timestamp: string
}

export type HealthState =
  | { phase: 'connecting' }
  | { phase: 'online'; units: number }
  | { phase: 'offline' }

export interface TitleHandlers {
  joinExpanded: boolean
  joinCode: string
  joinInputRef: React.RefObject<HTMLInputElement>
  toast: string | null
  health: HealthState
  /** Tri-state probe result. `null` while loading on first paint.
   *  After probing: `true` if the player has an active solo run,
   *  `false` otherwise (including the unauthenticated case). */
  hasActiveRun: boolean | null
  /** When true, the title renders a confirm overlay. The "or start
   *  new run" link sets this; the overlay's Cancel / Confirm buttons
   *  clear it. */
  confirmingNewRun: boolean

  startSolo: () => void
  continueRun: () => void
  requestNewRun: () => void
  cancelNewRun: () => void
  confirmNewRun: () => void
  openCompendium: () => void
  openMultiplayer: () => void
  expandJoin: () => void
  collapseJoin: () => void
  setJoinCode: (s: string) => void
  submitJoin: () => void
  showLobbyToast: () => void
}

const SOFT_TOAST = 'Lobbies are folding soon — singleplayer for now'

export function useTitleHandlers(): TitleHandlers {
  const [joinExpanded, setJoinExpanded] = useState(false)
  const [joinCode, setJoinCode] = useState('')
  const [toast, setToast] = useState<string | null>(null)
  const [health, setHealth] = useState<HealthState>({ phase: 'connecting' })
  const [hasActiveRun, setHasActiveRun] = useState<boolean | null>(null)
  const [confirmingNewRun, setConfirmingNewRun] = useState(false)
  const joinInputRef = useRef<HTMLInputElement>(null)
  const toastTimer = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/health')
      .then((r) => {
        if (!r.ok) throw new Error(`status ${r.status}`)
        return r.json() as Promise<HealthResponse>
      })
      .then((d) => {
        if (!cancelled) setHealth({ phase: 'online', units: d.units })
      })
      .catch(() => {
        if (!cancelled) setHealth({ phase: 'offline' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Probe the server once on mount to decide between "Begin Solo Run"
  // and "Continue Run + new run link". 404 (no active match) and 401
  // (unauthenticated) both resolve to hasActiveRun=false; only a 200
  // sets true.
  useEffect(() => {
    let cancelled = false
    getSoloSession()
      .then((r) => {
        if (cancelled) return
        setHasActiveRun(r.ok)
      })
      .catch(() => {
        if (!cancelled) setHasActiveRun(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const startSolo = useCallback(() => {
    window.location.search = '?solo'
  }, [])

  // Continue is structurally the same as start — /api/solo/start is
  // idempotent and resumes any active match. Kept as a distinct
  // handler so callers can express intent.
  const continueRun = useCallback(() => {
    window.location.search = '?solo'
  }, [])

  const requestNewRun = useCallback(() => {
    setConfirmingNewRun(true)
  }, [])

  const cancelNewRun = useCallback(() => {
    setConfirmingNewRun(false)
  }, [])

  const confirmNewRun = useCallback(async () => {
    // Abandon BEFORE logout — abandon needs the cookie to identify
    // the player's active match. After logout, the cookie is cleared,
    // SoloPlayScreen probes /api/player/me, gets 401, and routes to
    // PlayerCreationScreen so the user re-picks name + team + icon.
    await abandonSoloSession()
    await logoutPlayer()
    window.location.search = '?solo'
  }, [])

  const openCompendium = useCallback(() => {
    window.location.search = '?encyclopedia'
  }, [])

  const fireToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(null), 2600)
  }, [])

  const showLobbyToast = useCallback(() => fireToast(SOFT_TOAST), [fireToast])

  /** Navigate to the multiplayer entry (twin-scrolls overlay on the
   *  same title screen). Soft-navigates via pushState + url-changed
   *  so App.tsx re-routes without a hard page reload. The title
   *  remounts with `initialMode='multi'` and the scrolls animate
   *  themselves in. */
  const openMultiplayer = useCallback(() => {
    playSfx('title-cta')
    window.history.pushState(null, '', '?multi')
    window.dispatchEvent(new Event('url-changed'))
  }, [])

  const expandJoin = useCallback(() => {
    playSfx('title-cta')
    setJoinExpanded(true)
    window.setTimeout(() => joinInputRef.current?.focus(), 30)
  }, [])

  const collapseJoin = useCallback(() => {
    setJoinExpanded(false)
    setJoinCode('')
  }, [])

  const submitJoin = useCallback(() => {
    // Pass the entered code to the multi-entry overlay via
    // sessionStorage. The overlay's JoinInner reads it on mount and
    // auto-submits. Soft-nav (not window.location.search) so the
    // title's chrome doesn't flash white.
    const code = joinCode.trim().toUpperCase()
    if (code.length !== 6) return
    try { sessionStorage.setItem('origami-pending-join-code', code) } catch {}
    window.history.pushState(null, '', '?multi')
    window.dispatchEvent(new Event('url-changed'))
  }, [joinCode])

  useEffect(() => () => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current)
  }, [])

  // Escape still collapses the join-code input. No global key-to-start
  // shortcut on the candidate screens — the menu buttons are the
  // explicit entry points. Also closes the confirm-new-run overlay.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (confirmingNewRun) {
        setConfirmingNewRun(false)
        return
      }
      if (joinExpanded) collapseJoin()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [joinExpanded, collapseJoin, confirmingNewRun])

  return {
    joinExpanded,
    joinCode,
    joinInputRef,
    toast,
    health,
    hasActiveRun,
    confirmingNewRun,
    startSolo,
    continueRun,
    requestNewRun,
    cancelNewRun,
    confirmNewRun,
    openCompendium,
    openMultiplayer,
    expandJoin,
    collapseJoin,
    setJoinCode,
    submitJoin,
    showLobbyToast,
  }
}
